<template>
  <div class="flex flex-col p-3">
    <!-- Tabs -->
    <div class="border-b border-gray-200 mb-3">
      <nav class="flex space-x-6 px-4" aria-label="Tabs">
        <span :class="[tab === 'processes' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer']" @click="tab = 'processes'">Processes</span>
        <span :class="[tab === 'statistics' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer']"
              @click="selectStatisticsTab">Statistics</span>
      </nav>
    </div>

    <!-- Statistics tab -->
    <div v-if="tab === 'statistics'">
      <div v-if="stats" class="grid grid-cols-4 gap-3">
        <template v-for="(label, key) in statKeys" :key="key">
          <div v-if="stats[key]" class="flex flex-col p-3 stat-count shadow bg-white rounded-md">
            <div class="flex justify-between text-xs mb-2">
              <span class="text-gray-500">Pending</span><span class="font-bold text-gray-600">{{
                stats[key].pending
              }}</span>
            </div>
            <div class="flex justify-between text-xs mb-2">
              <span class="text-gray-500">Sent</span><span class="font-bold text-green-600">{{ stats[key].sent }}</span>
            </div>
            <div v-if="stats[key].in_progress !== undefined" class="flex justify-between text-xs mb-2">
              <span class="text-gray-500">In Progress</span><span
              class="font-bold text-blue-600">{{ stats[key].in_progress }}</span>
            </div>
            <div class="flex justify-between text-xs mb-2">
              <span class="text-gray-500">Paused</span><span class="font-bold text-blue-400">{{
                stats[key].paused
              }}</span>
            </div>
            <div class="flex justify-between text-xs mb-2">
              <span class="text-gray-500">Expired</span><span class="font-bold text-red-600">{{
                stats[key].expired
              }}</span>
            </div>
            <p class="text-xs font-bold text-center mt-1 border-t border-gray-100 pt-1">{{ label }}</p>
          </div>
        </template>
      </div>
      <div v-else class="text-center py-8 text-gray-400 text-sm">Loading statistics...</div>
    </div>

    <!-- Processes tab -->
    <div v-if="tab === 'processes'">
      <!-- Filter bar -->
      <div class="flex items-center gap-3 py-2 flex-wrap">
        <input type="text" v-model="search" placeholder="Search by BRN"
               class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary w-48"
               @keyup.enter="fetch" />
        <select v-model="statuses"
                class="px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                @change="fetch">
          <option :value="null">-- Filter by Status --</option>
          <option value="PENDING">Pending</option>
          <option value="PAUSED">Paused</option>
          <option value="IN_PROGRESS">In Progress</option>
          <option value="SENT">Sent</option>
          <option value="EXPIRED">Expired</option>
        </select>
        <select v-model="types"
                class="px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                @change="fetch">
          <option :value="null">-- Filter by Type --</option>
          <option
            v-for="option in typeOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.text }}
          </option>
        </select>
        <input type="date" v-model="startDate"
               class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
               @change="fetch" />
        <input type="date" v-model="endDate"
               class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
               @change="fetch" />
        <select v-model="limit"
                class="px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                @change="fetch">
          <option :value="12">12</option>
          <option :value="20">20</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
      </div>

      <!-- Table -->
      <div>
        <table>
          <thead>
          <tr>
            <th>#</th>
            <th>BRN</th>
            <th>Type</th>
            <th>Document Name</th>
            <th>Retry Count</th>
            <th>Status</th>
            <th>Date</th>
            <th>Last Ran</th>
            <th>Action</th>
          </tr>
          </thead>
          <tbody>
          <template v-if="loading">
            <tr v-for="i in 6" :key="`sk-${i}`" class="animate-pulse">
              <td v-for="j in 9" :key="j">
                <div class="h-3 bg-gray-200 rounded"></div>
              </td>
            </tr>
          </template>
          <template v-else>
            <tr v-for="(action, idx) in stream" :key="idx">
              <td>{{ (currentPage - 1) * limit + idx + 1 }}</td>
              <td class="font-bold font-mono cursor-pointer hover:underline text-primary"
                  @click="searchTerm(action.brn)">{{ action.brn }}
              </td>
              <td class="italic text-blue-600 cursor-pointer hover:underline" @click="searchTerm(action.type)">
                {{ action.type }}
              </td>
              <td class="font-mono text-xs whitespace-nowrap">{{ action.actionIdToken || "—" }}</td>
              <td class="text-center">{{ action.retryCount }}</td>
              <td><span :class="statusPill(action.status)">{{ action.status?.toLowerCase() }}</span></td>
              <td class="text-xs">{{ dateFormat(action.createdAt ?? null) }}</td>
              <td class="text-xs">{{ dateFormat(action.lastRanAt ?? null) }}</td>
              <td>
                <div class="flex gap-1">
                  <button v-if="action.status === 'EXPIRED' || action.status === 'PAUSED'" class="btn-sm-danger disabled:opacity-50"
                          :disabled="!!processingState[action.id]"
                          @click="redo(action)">
                    <i class="fa mr-1" :class="processingState[action.id] === 'retry' ? 'fa-spinner fa-spin' : 'fa-redo'"></i> Retry
                  </button>
                  <button
                    v-if="canReplaceSignedCopy(action)"
                    type="button"
                    class="bg-amber-600 hover:bg-amber-700 text-white px-2 py-1 text-xs font-medium rounded disabled:opacity-50"
                    :disabled="!!processingState[action.id]"
                    @click="openReplace(action)"
                  >
                    <i class="fa fa-exchange-alt mr-1"></i> Replace
                  </button>
                  <button class="btn-sm-primary" @click="view(action)" :disabled="!!processingState[action.id]">
                    <i class="fa fa-eye mr-1"></i> View
                  </button>
                  <button class="bg-green-600 hover:bg-green-700 text-white px-2 py-1 text-xs font-medium rounded disabled:opacity-50 flex items-center transition"
                          :disabled="!!processingState[action.id]"
                          @click="runNow(action)">
                    <i class="fa mr-1" :class="processingState[action.id] === 'run' ? 'fa-spinner fa-spin' : 'fa-play'"></i> 
                    {{ processingState[action.id] === 'run' ? 'Running...' : 'Run' }}
                  </button>
                </div>
              </td>
            </tr>
          </template>
          <tr v-if="!loading && stream.length === 0">
            <td colspan="9" class="text-center py-6 text-gray-400 text-sm">No results found</td>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.totalPages > 1"
           class="flex items-center justify-between px-1 py-3 border-t border-gray-100 mt-2">
        <p class="text-xs text-gray-500">Page <span class="font-semibold text-gray-700">{{ currentPage }}</span> of
          <span class="font-semibold text-gray-700">{{ pagination.totalPages }}</span></p>
        <div class="flex gap-2">
          <button class="btn-sm-primary" @click="previous" :disabled="currentPage === 1">
            <i class="fa fa-chevron-left mr-1"></i> Prev
          </button>
          <button class="btn-sm-primary" @click="next" :disabled="currentPage === pagination.totalPages">
            Next <i class="fa fa-chevron-right ml-1"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Replace signed copy modal -->
    <AppModal v-model="showReplaceModal" title="Replace Signed Copy" hide-footer>
      <form class="p-4 space-y-4" @submit.prevent="submitReplace">
        <div class="flex flex-col gap-1">
          <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">File name</label>
          <input
            v-model="replaceForm.fileName"
            type="text"
            disabled
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Reason</label>
          <textarea
            v-model="replaceForm.reason"
            rows="3"
            required
            placeholder="Reason for replacing the signed copy"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50 resize-none"
          ></textarea>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Replacement file</label>
          <input
            type="file"
            accept=".pdf,application/pdf"
            required
            class="block w-full text-sm text-gray-600 file:mr-2 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary file:text-white hover:file:bg-primary-600 file:cursor-pointer border border-gray-300 rounded-md bg-gray-50"
            @change="onReplaceFileChange"
          />
          <p class="text-xs text-gray-400">Upload the corrected signed PDF</p>
        </div>
        <div class="border-t border-gray-100 pt-3 flex justify-end gap-2">
          <button type="button" class="btn-sm-primary" :disabled="replacing" @click="showReplaceModal = false">
            Cancel
          </button>
          <button type="submit" class="btn-primary" :disabled="replacing">
            <i v-if="replacing" class="fa fa-spinner fa-spin mr-1"></i>
            <i v-else class="fa fa-exchange-alt mr-1"></i>
            <span>{{ replacing ? "Replacing..." : "Replace" }}</span>
          </button>
        </div>
      </form>
    </AppModal>

    <!-- Detail Modal -->
    <AppModal v-model="showDetails" size="xl" title="Process Details" hide-footer>
      <div v-if="process" class="space-y-5 text-sm p-1 max-h-[80vh] overflow-y-auto">
        <!-- Status Bar & Basic Info -->
        <div class="bg-slate-50 border border-slate-200/60 rounded-xl p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <span class="text-[10px] text-slate-400 uppercase tracking-wider font-bold block">Status</span>
            <div class="mt-1">
              <span :class="statusBadgeClass(process.status)">
                {{ process.status }}
              </span>
            </div>
          </div>
          <div>
            <span class="text-[10px] text-slate-400 uppercase tracking-wider font-bold block">Type</span>
            <div class="mt-1 font-semibold text-slate-850 break-words text-xs py-0.5">{{ process.type }}</div>
          </div>
          <div>
            <span class="text-[10px] text-slate-400 uppercase tracking-wider font-bold block">Action ID Token</span>
            <div class="mt-1 font-mono text-xs text-slate-700 bg-slate-100 px-2 py-0.5 rounded w-max border border-slate-200/40">
              {{ process.actionIdToken || 'N/A' }}
            </div>
          </div>
          <div>
            <span class="text-[10px] text-slate-400 uppercase tracking-wider font-bold block">Retries</span>
            <div class="mt-1 font-medium text-slate-800">
              {{ process.retryCount }} retries
            </div>
          </div>
        </div>

        <!-- Description -->
        <div class="border-l-4 border-amber-500 bg-amber-50/50 p-4 rounded-r-xl border-y border-r border-amber-200/20">
          <h4 class="text-xs font-bold text-amber-800 uppercase tracking-wider flex items-center gap-1.5 mb-1">
            <i class="fa-solid fa-circle-info"></i>
            Description
          </h4>
          <p class="text-slate-700 leading-relaxed text-xs">{{ process.description }}</p>
        </div>

        <!-- Dynamic Detailed Content -->
        <div v-if="loadingDetails" class="flex flex-col items-center justify-center py-10 border border-dashed border-slate-200 rounded-xl bg-slate-50/20">
          <i class="fa-solid fa-circle-notch fa-spin text-2xl text-blue-600 mb-2"></i>
          <span class="text-xs text-slate-400">Loading complete process details...</span>
        </div>

        <div v-else class="space-y-5">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <!-- Owner (Company) Details Card -->
            <div v-if="selectedDetails.owner" class="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col justify-between">
              <div class="bg-slate-50 border-b border-slate-150 px-4 py-3 flex items-center justify-between">
                <h3 class="font-bold text-slate-800 text-xs flex items-center gap-2">
                  <i class="fa-solid fa-building text-slate-500 text-sm"></i>
                  Owner / Company Details
                </h3>
                <span v-if="selectedDetails.owner.status" class="text-[10px] px-2 py-0.5 rounded-full font-bold bg-blue-50 text-blue-700 border border-blue-100 uppercase">
                  {{ selectedDetails.owner.status }}
                </span>
              </div>
              <div class="p-4 space-y-3 flex-1 flex flex-col justify-center">
                <div class="flex justify-between items-start border-b border-slate-100 pb-2">
                  <span class="text-xs text-slate-400">Name</span>
                  <span class="font-bold text-slate-880 text-right text-xs max-w-[70%] leading-tight">{{ selectedDetails.owner.name }}</span>
                </div>
                <div class="flex justify-between items-center border-b border-slate-100 pb-2">
                  <span class="text-xs text-slate-400">BRN</span>
                  <span class="font-mono text-xs font-bold text-slate-750 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-150">{{ selectedDetails.owner.brn }}</span>
                </div>
                <div class="flex justify-between items-center border-b border-slate-100 pb-2">
                  <span class="text-xs text-slate-400">Entity Type</span>
                  <span class="text-xs font-semibold text-slate-700 capitalize">{{ selectedDetails.owner.entityType?.replace('-', ' ') }}</span>
                </div>
                <div class="flex justify-between items-center border-b border-slate-100 pb-2" v-if="selectedDetails.owner.subEntityType">
                  <span class="text-xs text-slate-400">Sub-Type</span>
                  <span class="text-xs font-semibold text-slate-700 capitalize">{{ selectedDetails.owner.subEntityType?.split('-').join(' ') }}</span>
                </div>
                <div class="flex justify-between items-center border-b border-slate-100 pb-2" v-if="selectedDetails.owner.registrationDate">
                  <span class="text-xs text-slate-400">Registered On</span>
                  <span class="text-xs font-medium text-slate-750">{{ selectedDetails.owner.registrationDate }}</span>
                </div>
                <div class="flex justify-between items-center border-b border-slate-100 pb-2" v-if="selectedDetails.owner.primaryEmail">
                  <span class="text-xs text-slate-400">Email</span>
                  <a :href="'mailto:' + selectedDetails.owner.primaryEmail" class="text-xs text-blue-600 hover:underline hover:text-blue-800 font-medium">{{ selectedDetails.owner.primaryEmail }}</a>
                </div>
                <div class="flex justify-between items-center" v-if="selectedDetails.owner.primaryPhone">
                  <span class="text-xs text-slate-400">Phone</span>
                  <span class="text-xs font-medium text-slate-750">{{ selectedDetails.owner.primaryPhone }}</span>
                </div>
              </div>
            </div>

            <!-- Initiator Details Card -->
            <div v-if="selectedDetails.initiator" class="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col justify-between">
              <div class="bg-slate-50 border-b border-slate-150 px-4 py-3">
                <h3 class="font-bold text-slate-800 text-xs flex items-center gap-2">
                  <i class="fa-solid fa-user-circle text-slate-500 text-sm"></i>
                  Initiator Details
                </h3>
              </div>
              <div class="p-4 space-y-3 flex-1 flex flex-col justify-center">
                <div class="flex justify-between items-center border-b border-slate-100 pb-2">
                  <span class="text-xs text-slate-400">Full Name</span>
                  <span class="font-bold text-slate-880 text-xs">{{ selectedDetails.initiator.fullName || (selectedDetails.initiator.givenName + ' ' + selectedDetails.initiator.surName) }}</span>
                </div>
                <div class="flex justify-between items-center border-b border-slate-100 pb-2" v-if="selectedDetails.initiator.nin">
                  <span class="text-xs text-slate-400">NIN</span>
                  <span class="font-mono text-xs font-bold text-slate-750 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-150">{{ selectedDetails.initiator.nin }}</span>
                </div>
                <div class="flex justify-between items-center border-b border-slate-100 pb-2" v-if="selectedDetails.initiator.email">
                  <span class="text-xs text-slate-400">Email</span>
                  <a :href="'mailto:' + selectedDetails.initiator.email" class="text-xs text-blue-600 hover:underline hover:text-blue-800 font-medium">{{ selectedDetails.initiator.email }}</a>
                </div>
                <div class="flex justify-between items-center" v-if="selectedDetails.initiator.phoneNumber">
                  <span class="text-xs text-slate-400">Phone</span>
                  <span class="text-xs font-medium text-slate-750">{{ selectedDetails.initiator.phoneNumber }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Timeline & Tech Specs -->
          <div class="bg-slate-50 border border-slate-200/60 rounded-xl p-4 space-y-3">
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-slate-500">
              <div class="flex items-center gap-1.5">
                <i class="fa-regular fa-clock text-slate-400"></i>
                <span>Created: <b class="text-slate-700">{{ dateFormat(selectedDetails.createdAt ?? null) }}</b></span>
              </div>
              <div class="flex items-center gap-1.5" v-if="selectedDetails.lastRanAt">
                <i class="fa-solid fa-play text-slate-400"></i>
                <span>Last Ran: <b class="text-slate-700">{{ dateFormat(selectedDetails.lastRanAt) }}</b></span>
              </div>
              <div class="flex items-center gap-1.5" v-if="selectedDetails.updatedAt">
                <i class="fa-regular fa-calendar-check text-slate-400"></i>
                <span>Updated: <b class="text-slate-700">{{ dateFormat(selectedDetails.updatedAt) }}</b></span>
              </div>
            </div>
            <div v-if="selectedDetails.endPoint" class="pt-3 border-t border-slate-200 flex flex-col gap-1.5">
              <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Endpoint Path</span>
              <div class="flex items-center gap-2">
                <code class="text-xs bg-white border border-slate-200 px-3 py-1.5 rounded-lg font-mono text-slate-800 w-full overflow-x-auto select-all shadow-inner">{{ selectedDetails.endPoint }}</code>
              </div>
            </div>
          </div>

          <!-- Request Payload -->
          <div v-if="selectedDetails.request">
            <div class="flex items-center justify-between mb-2">
              <h4 class="font-bold text-slate-800 text-xs flex items-center gap-2">
                <i class="fa-solid fa-arrow-right-to-bracket text-emerald-600"></i>
                Request Payload (JSON)
              </h4>
              <button
                @click="copyText(selectedDetails.request)"
                class="text-xs text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1 border border-blue-200/50 bg-blue-50/30 hover:bg-blue-50 px-2 py-1 rounded transition"
              >
                <i class="fa-regular fa-copy"></i> Copy
              </button>
            </div>
            <pre class="text-slate-300 text-[11px] bg-slate-900 p-4 rounded-xl overflow-auto max-h-56 font-mono leading-relaxed shadow-lg">{{
                formatJson(selectedDetails.request)
              }}</pre>
          </div>

          <!-- Response Payload -->
          <div v-if="selectedDetails.response">
            <div class="flex items-center justify-between mb-2">
              <h4 class="font-bold text-slate-800 text-xs flex items-center gap-2">
                <i class="fa-solid fa-arrow-right-from-bracket text-rose-600"></i>
                Response Payload / Error
              </h4>
              <button
                @click="copyText(selectedDetails.response)"
                class="text-xs text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1 border border-blue-200/50 bg-blue-50/30 hover:bg-blue-50 px-2 py-1 rounded transition"
              >
                <i class="fa-regular fa-copy"></i> Copy
              </button>
            </div>
            <pre class="text-rose-700 text-[11px] bg-rose-50/50 border border-rose-150 p-4 rounded-xl overflow-auto max-h-72 font-mono whitespace-pre-wrap leading-relaxed shadow-sm">{{
                selectedDetails.response
              }}</pre>
          </div>
        </div>
      </div>
    </AppModal>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useAbility } from "@casl/vue";
import AppModal from "@/components/ui/AppModal.vue";
import { dateFormat } from "@/commons/composables/utilities.ts";
import { useToast } from "@/composables";
import { useInternalProcessesStore } from "../stores";
import type { IncorporationProcessAction } from "../types";

const store = useInternalProcessesStore();
const { successToast, errorToast, warningToast } = useToast();
const { can } = useAbility();

const canViewMenuItem = (permission: string) => can(permission, "general") === true;

const tab = ref("processes");
const showDetails = ref(false);
const showReplaceModal = ref(false);
const replacing = ref(false);
const loading = ref(false);
const currentPage = ref(1);
const limit = ref(12);
const stream = ref<IncorporationProcessAction[]>([]);
const pagination = ref({ totalElements: 0, totalPages: 0 });
const statuses = ref<string | null>(null);
const types = ref<string | null>(null);
const search = ref("");
const startDate = ref<string | null>(null);
const endDate = ref<string | null>(null);
const process = ref<IncorporationProcessAction | null>(null);
const stats = ref<Record<string, Record<string, number>> | null>(null);
const processingState = ref<Record<string, 'run' | 'retry'>>({});

const replaceForm = ref({
  fileName: "",
  reason: "",
  file: null as File | null,
});

const typeOptions = [
  { value: "EDMS", text: "EDMS" },
  { value: "CERTIFICATE", text: "Certificate" },
  { value: "BUSINESS_ACCOUNT", text: "Business Account" },
  { value: "BUSINESS_ACCOUNT_NAME_CHANGE", text: "Business Account Name Change" },
  { value: "DIGITAL_SIGNING", text: "Digital Signing" },
  { value: "LEGAL_DOCUMENTS", text: "Legal Documents" },
  { value: "INSTANT_TIN", text: "Instant TIN" },
  { value: "BUSINESS_ACCOUNT_SUBTYPE", text: "Business Account Subtype" },
  { value: "CENTRAL_REGISTRY", text: "Central Registry" },
  { value: "PUSH_DOCUMENT_ID_TO_REGISTRY", text: "Push Document ID To Registry" },
  { value: "REGISTRY_UPDATE", text: "Registry Update" },
  { value: "FETCH_REGISTRY", text: "Fetch Registry" },
  { value: "RECONCILE_PAYMENTS", text: "Reconcile Payments" },
  { value: "NIN_PHOTOS", text: "NIN Photos" },
  { value: "BENEFICIAL_OWNERS", text: "Beneficial Owners" },
  { value: "CHECK_POSTAL_ADDRESS_COMPLIANCE", text: "Check Postal Address Compliance" },
  { value: "POLL_POSTAL_ADDRESS_RENEWAL", text: "Poll Postal Address Renewal" },
  { value: "ISSUE_CERTIFICATE_OF_DISSOLUTION", text: "Issue Certificate Of Dissolution" },
  { value: "NON_INDIVIDUAL_REGISTRY", text: "Non-individual Registry" },
  { value: "REGISTER_WITH_NIR", text: "Register With NIR" },
  { value: "NIR_NAME_CHANGE", text: "NIR Name Change" },
  { value: "NIR_SUBTYPE_CHANGE", text: "NIR Subtype Change" },
  { value: "NIR_COMPLIANCE_UPDATE", text: "NIR Compliance Update" },
  { value: "NIR_STATUS_UPDATE", text: "NIR Status Update" }
] as const;

const statKeys: Record<string, string> = {
  all: "All",
  certificates: "Certificates",
  digitalSigning: "Digital Signing",
  edms: "EDMS",
  businessAccount: "Business Account",
  businessAccountSubType: "Business Account SubType",
  businessAccountName: "Business Account Name",
  centralRegistry: "Registry",
  pushDocumentIdToCentralRegistry: "DocumentId To Registry",
  legalDocuments: "Legal Documents",
  instantTin: "Instant TIN",
  updateCentralRegistry: "Update Registry"
};

const statusPill = (status: string) => {
  const base = "text-xs px-2 py-0.5 rounded-full capitalize ";
  if (status === "SENT") return base + "bg-green-50 text-green-700";
  if (status === "EXPIRED") return base + "bg-red-50 text-red-700";
  if (status === "IN_PROGRESS") return base + "bg-blue-50 text-blue-700";
  if (status === "PAUSED") return base + "bg-orange-50 text-orange-600";
  if (status === "PENDING") return base + "bg-gray-100 text-gray-600";
  return base + "bg-gray-100 text-gray-600";
};

const fetch = () => {
  loading.value = true;
  const resolvedStatuses = statuses.value ?? "";
  if (search.value) currentPage.value = 1;
  store.fetchIncorporationProcessesStream({
    statuses: resolvedStatuses,
    types: types.value ?? undefined,
    page: currentPage.value,
    limit: limit.value,
    search: search.value,
    startDate: startDate.value,
    endDate: endDate.value
  });
};

const selectStatisticsTab = () => {
  tab.value = "statistics";
  store.fetchIncorporationProcessStatistics();
};

const searchTerm = (term: string) => {
  search.value = term;
  fetch();
};

const canReplaceSignedCopy = (action: IncorporationProcessAction) => {
  if (!canViewMenuItem("can_replace_signed_copy")) return false;
  return action.type === "DIGITAL_SIGNING";
};

const resetReplaceForm = () => {
  replaceForm.value = { fileName: "", reason: "", file: null };
};

const openReplace = (action: IncorporationProcessAction) => {
  if (!canReplaceSignedCopy(action)) return;
  replaceForm.value = {
    fileName: action.fileName != null ? String(action.fileName) : "",
    reason: "",
    file: null,
  };
  showReplaceModal.value = true;
};

const onReplaceFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  replaceForm.value.file = target.files?.[0] ?? null;
};

const submitReplace = () => {
  const reason = replaceForm.value.reason.trim();
  const fileName = replaceForm.value.fileName.trim();
  if (!fileName) {
    warningToast("Validation", "File name is required");
    return;
  }
  if (!reason) {
    warningToast("Validation", "Enter a reason for the replacement");
    return;
  }
  if (!replaceForm.value.file) {
    warningToast("Validation", "Select a replacement PDF file");
    return;
  }
  replacing.value = true;
  store.replaceSignedCopy({
    file: replaceForm.value.file,
    reason,
    fileName,
  });
};

const view = (details: IncorporationProcessAction) => {
  store.incorporationDetails = null;
  process.value = details;
  showDetails.value = true;
  store.fetchIncorporationProcessDetails({ id: details.id });
};

const redo = (action: IncorporationProcessAction) => {
  processingState.value[action.id] = 'retry';
  store.updateProcessStatus({ id: action.id, status: "PENDING" });
};

const runNow = (action: IncorporationProcessAction) => {
  processingState.value[action.id] = 'run';
  store.runProcessNow({ actionId: action.id });
};

const next = () => {
  if (currentPage.value < pagination.value.totalPages) {
    currentPage.value++;
    fetch();
  }
};
const previous = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    fetch();
  }
};

onMounted(() => fetch());

watch(() => store.incorporationStream, (data) => {
  loading.value = false;
  if (data?.content) {
    stream.value = data.content;
    pagination.value = { totalPages: data.totalPages, totalElements: data.totalElements };
  }
});

watch(() => store.incorporationStats, (data) => {
  if (data) stats.value = data;
});

watch(() => store.incorporationStatusChange, (data) => {
  if (data) {
    processingState.value = {};
    fetch();
  }
});

watch(() => store.incorporationStatusError, (error) => {
  if (error) {
    processingState.value = {};
    errorToast("Error", error.message || "Action failed");
  }
});

watch(() => store.replaceSignedCopyStatusChange, (data) => {
  if (!data) return;
  replacing.value = false;
  successToast("Success", data.message || "Signed copy replaced successfully");
  showReplaceModal.value = false;
  resetReplaceForm();
  fetch();
});

watch(() => store.replaceSignedCopyError, (error) => {
  if (!error) return;
  replacing.value = false;
  errorToast("Error", error.message || "Could not replace signed copy");
});

const loadingDetails = computed(() => {
  return !store.incorporationDetails || store.incorporationDetails.data?.id !== process.value?.id;
});

const selectedDetails = computed(() => {
  if (store.incorporationDetails?.data?.id === process.value?.id) {
    return store.incorporationDetails.data;
  }
  return process.value;
});

const formatJson = (jsonString: string) => {
  if (!jsonString) return "";
  try {
    return JSON.stringify(JSON.parse(jsonString), null, 2);
  } catch (e) {
    return jsonString;
  }
};

const copyText = (text: string) => {
  if (!text) return;
  navigator.clipboard.writeText(text);
  successToast("Copied", "Copied to clipboard successfully!");
};

const statusBadgeClass = (status: string) => {
  if (!status) return "";
  switch (status.toUpperCase()) {
    case "SUCCESS":
    case "COMPLETED":
      return "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200 shadow-sm";
    case "PAUSED":
      return "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200 shadow-sm animate-pulse";
    case "FAILED":
    case "ERROR":
      return "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 border border-rose-200 shadow-sm";
    case "PENDING":
    case "RUNNING":
      return "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200 shadow-sm animate-pulse";
    default:
      return "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-800 border border-slate-200 shadow-sm";
  }
};
</script>
